import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Send httpOnly cookies with every request
});

// --- Access token management (set by AuthContext) ---
let _currentAccessToken = null;

/**
 * Called by AuthContext to set the current access token.
 * The token lives in memory (JS variable), NOT localStorage.
 */
api._setAccessToken = (token) => {
  _currentAccessToken = token;
};

// These are set by AuthContext after mount
api._refreshAccessToken = null;
api._handleAuthFailure = null;

// --- Request interceptor: attach Bearer token ---
api.interceptors.request.use((config) => {
  if (_currentAccessToken) {
    config.headers.Authorization = `Bearer ${_currentAccessToken}`;
  }
  return config;
});

// --- Response interceptor: handle 401 with token refresh ---
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Network errors (no response) are transient — likely a Cloud Run cold start.
    // Pass them through without triggering auth failure or logout.
    if (!error.response) {
      return Promise.reject(error);
    }

    // Only retry on 401 and if we haven't already retried this request
    if (error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Don't retry auth endpoints themselves
    const path = originalRequest.url || '';
    if (path.includes('/auth/refresh') || path.includes('/login') || path.includes('/signup')) {
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const newToken = await api._refreshAccessToken();
      processQueue(null, newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      // Only force logout if the refresh endpoint itself returned a real 401
      // (server explicitly rejected the token). Don't logout on network errors
      // (cold start timeout) or 5xx (server crash) — those are transient.
      const isRealAuthFailure = refreshError?.response?.status === 401;
      if (isRealAuthFailure) {
        if (api._handleAuthFailure) {
          api._handleAuthFailure();
        }
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);


// --- Utility helpers ---
const uniqueList = (values) => {
  const seen = new Set();
  return values.filter(item => {
    if (!item) return false;
    const key = typeof item === 'object' ? (item.skill || item.bullet || JSON.stringify(item)) : String(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
const waitMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const buildJobDescriptionPayload = (jobDescriptionText) => {
  const trimmedText = jobDescriptionText.trim();

  // Send the full raw JD text to the backend. The backend's AI-powered
  // JD parsing service will extract job_title, company_name, skills,
  // seniority_level, and other structured fields with far higher accuracy
  // than any client-side regex could achieve. Passing empty structured
  // fields signals the backend to auto-enrich before scoring.
  return {
    job_title: '',
    company_name: '',
    description: trimmedText,
    required_skills: [],
    preferred_skills: [],
    seniority_level: '',
  };
};

const mapAnalysisResponse = (payload) => {
  const aiScore = payload?.ai_score || {};
  const overall = Math.round(aiScore.overall_match_percentage || 0);
  const hardSkillGaps = aiScore.keyword_gaps?.missing_hard_skills || [];
  const softSkillGaps = aiScore.keyword_gaps?.missing_soft_skills || [];
  const breakdown = aiScore.detailed_breakdown || {};
  const breakdownReasons = aiScore.detailed_breakdown_reasons || {};
  const persistedRecommendations = Array.isArray(payload?.recommendations)
    ? payload.recommendations.filter(Boolean)
    : [];
  const synthesizedRecommendations = [
    ...hardSkillGaps.map((skill) => ({
      skill,
      bullet: `You likely have this skill already. Prove it with a concrete project win: ${skill}`,
      relevant_section_ids: []
    })),
    ...softSkillGaps.map((skill) => ({
      skill,
      bullet: `Bring this strength to life with a real collaboration story: ${skill}`,
      relevant_section_ids: []
    })),
    (aiScore.formatting_score || 0) < 90 ? {
      skill: null,
      bullet: 'Your content is strong. Now polish structure and readability so ATS systems and humans both scan it faster.',
      relevant_section_ids: []
    } : null,
  ].filter(Boolean);

  return {
    analysis_id: payload.analysis_id,
    scores: {
      overall,
      skill_match: Math.round(breakdown.skill_match ?? 0),
      experience_match: Math.round(breakdown.experience_match ?? 0),
      education_match: Math.round(breakdown.education_match ?? 0),
      cultural_fit: Math.round(breakdown.cultural_fit ?? 0),
    },
    breakdown_reasons: {
      skill_match: breakdownReasons.skill_match || '',
      experience_match: breakdownReasons.experience_match || '',
      education_match: breakdownReasons.education_match || '',
      cultural_fit: breakdownReasons.cultural_fit || '',
    },
    ai_summary:
      aiScore.experience_alignment?.details ||
      'Resume analysis completed successfully against the provided job description.',
    recommendations: uniqueList(persistedRecommendations.length ? persistedRecommendations : synthesizedRecommendations),
    raw: payload,
  };
};


// --- API modules ---

// Authentication (kept for direct usage, but AuthContext is preferred)
export const authAPI = {
  signup: (data) => api.post('/signup', data),
  login: (email, password) => api.post('/login', { email, password }),
};

// Resume Operations — NO MORE user_id in requests (comes from JWT)
export const resumeAPI = {
  upload: async (formData) => {
    // user_id removed — backend extracts it from the JWT token
    const response = await api.post('/resume/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    return {
      ...response,
      data: {
        ...response.data,
        resume_id: response.data.analysis_id,
      },
    };
  },
  getHistory: () => api.get('/resume/history'),  // No user_id param — JWT provides identity
  getResume: (analysisId) => api.get(`/resume/status/${analysisId}`),
  atsRewrite: (formData) => api.post('/resume/rewrite', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Analysis Operations — NO MORE user_id in requests
export const analysisAPI = {
  score: async (analysisId, jobRequirements) => {
    // user_id removed from payload — backend extracts it from the JWT token
    const response = await api.post(`/resume/score/${analysisId}`, {
      job_description: buildJobDescriptionPayload(jobRequirements),
    });

    return {
      ...response,
      data: mapAnalysisResponse(response.data),
    };
  },
};

export const optimizeAPI = {
  analyzeStrategy: async (analysisId) => {
    return api.post('/optimize/analyze-strategy', {
      analysis_id: analysisId,
    });
  },

  createJob: async (analysisId, jobRequirements, tone = 'professional', claimedSkills = [], sectionModes = null) => {
    // user_id removed — backend extracts it from the JWT token
    const payload = {
      analysis_id: analysisId,
      job_description: buildJobDescriptionPayload(jobRequirements),
      tone,
      claimed_skills: claimedSkills,
    };
    if (sectionModes && Object.keys(sectionModes).length > 0) {
      payload.section_modes = sectionModes;
    }
    return api.post('/optimize/jobs', payload);
  },

  getJobStatus: async (jobId) => {
    // user_id removed from query params — backend extracts it from the JWT token
    return api.get(`/optimize/jobs/${jobId}`);
  },

  getJobResult: async (jobId) => {
    return api.get(`/optimize/jobs/${jobId}/result`);
  },

  retryJob: async (jobId) => {
    return api.post(`/optimize/jobs/${jobId}/retry`);
  },

  downloadPdf: async (jobId) => {
    return api.get(`/artifacts/${jobId}/download-pdf`, { responseType: 'blob' });
  },

  waitForCompletion: async (jobId, onProgress, timeoutMs = 300000) => {
    const startedAt = Date.now();
    let pollDelayMs = 3500;
    const maxPollDelayMs = 10000;

    while (Date.now() - startedAt < timeoutMs) {
      let statusData;
      try {
        const statusResponse = await optimizeAPI.getJobStatus(jobId);
        statusData = statusResponse.data;
        pollDelayMs = 3500;
      } catch (error) {
        if (error?.response?.status === 429) {
          pollDelayMs = Math.min(maxPollDelayMs, pollDelayMs + 1500);
          await waitMs(pollDelayMs);
          continue;
        }
        throw error;
      }

      if (typeof onProgress === 'function') {
        onProgress(statusData);
      }

      if (statusData.status === 'completed') {
        const resultResponse = await optimizeAPI.getJobResult(jobId);
        return resultResponse.data;
      }

      if (statusData.status === 'failed') {
        throw new Error(statusData.error || 'Optimization failed');
      }

      await waitMs(pollDelayMs);
    }

    throw new Error('Optimization timed out. You can retry from the dashboard.');
  },
};

export const trackerAPI = {
  saveSnapshot: async (optimizationJobId) => {
    return api.post('/tracker/save', { optimization_job_id: optimizationJobId });
  },
  getHistory: async (limit = 50, skip = 0) => {
    return api.get(`/tracker/history?limit=${limit}&skip=${skip}`);
  },
  deleteSnapshot: async (snapshotId) => {
    return api.delete(`/tracker/${snapshotId}`);
  },
  getDownloadUrl: async (artifactId) => {
    return api.get(`/artifacts/${artifactId}/download`);
  },
  downloadPdf: async (jobId) => {
    return api.get(`/artifacts/${jobId}/download-pdf`, { responseType: 'blob' });
  },
  updateSnapshot: async (snapshotId, data) => {
    return api.patch(`/tracker/${snapshotId}`, data);
  }
};

// Utility: Extract JD from URL by calling the backend extraction service
export const extractJDFromUrl = async (url) => {
  try {
    const response = await api.post('/tools/extract-jd', { url });
    return response.data.text;
  } catch (error) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error('Failed to extract JD from URL. Please paste the text manually.');
  }
};

// Contact & Support Operations
export const contactAPI = {
  sendEmail: async (subject, message) => {
    return api.post('/tools/contact', { subject, message });
  }
};

export default api;